import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import Pagination from '../components/ui/Pagination';
import { Button } from '../components/ui/Button';
import { jobListings, JobListing } from '../data/mockData';
import { BriefcaseIcon, MapPinIcon, ClockIcon, FilterIcon, XIcon, SearchIcon, ChevronDownIcon, CheckCircleIcon } from 'lucide-react';
const Jobs = () => {
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 6;
  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    locationType: [] as string[],
    employmentType: [] as string[],
    experienceLevel: [] as string[],
    featured: false
  });
  // State for showing/hiding filters on mobile
  const [showFilters, setShowFilters] = useState(false);
  // Apply filters to job listings
  const filteredJobs = jobListings.filter(job => {
    // Search filter
    if (filters.search && !job.title.toLowerCase().includes(filters.search.toLowerCase()) && !job.company.name.toLowerCase().includes(filters.search.toLowerCase()) && !job.skills.some(skill => skill.toLowerCase().includes(filters.search.toLowerCase()))) {
      return false;
    }
    // Location type filter
    if (filters.locationType.length > 0 && !filters.locationType.includes(job.locationType)) {
      return false;
    }
    // Employment type filter
    if (filters.employmentType.length > 0 && !filters.employmentType.includes(job.employmentType)) {
      return false;
    }
    // Experience level filter
    if (filters.experienceLevel.length > 0 && !filters.experienceLevel.includes(job.experienceLevel)) {
      return false;
    }
    // Featured filter
    if (filters.featured && !job.featured) {
      return false;
    }
    return true;
  });
  // Get current jobs for pagination
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);
  // Handle filter changes
  const handleFilterChange = (filterType: keyof typeof filters, value: string | boolean) => {
    if (filterType === 'locationType' || filterType === 'employmentType' || filterType === 'experienceLevel') {
      setFilters(prevFilters => {
        const currentValues = prevFilters[filterType] as string[];
        if (currentValues.includes(value as string)) {
          return {
            ...prevFilters,
            [filterType]: currentValues.filter(v => v !== value)
          };
        } else {
          return {
            ...prevFilters,
            [filterType]: [...currentValues, value as string]
          };
        }
      });
    } else {
      setFilters(prevFilters => ({
        ...prevFilters,
        [filterType]: value
      }));
    }
  };
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      locationType: [],
      employmentType: [],
      experienceLevel: [],
      featured: false
    });
  };
  // Format date to relative time (e.g., "2 days ago")
  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
  };
  // Format salary range
  const formatSalary = (job: JobListing) => {
    const {
      min,
      max,
      currency
    } = job.salaryRange;
    // Check if it's hourly rate or annual salary
    if (min < 1000) {
      return `${currency}${min}-${max}/hr`;
    }
    // Format annual salary with K or M
    const formatNumber = (num: number) => {
      if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M`;
      }
      return `${Math.round(num / 1000)}K`;
    };
    return `${currency}${formatNumber(min)}-${formatNumber(max)}`;
  };
  return <div className="bg-[#0F1115] min-h-screen text-gray-300 relative" data-id="element-1243">
      {/* Global Grain Texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay" data-id="element-1244"></div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F1115] via-[#1a1d24] to-[#111827] text-white overflow-hidden" data-id="element-1245">
        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.015] bg-grain" data-id="element-1246"></div>

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1247">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#d73b3e]/10 to-transparent rounded-full blur-3xl" data-id="element-1248" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full blur-3xl" data-id="element-1249" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10" data-id="element-1250">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1251">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6" data-id="element-1252">
              <BriefcaseIcon className="w-4 h-4 text-[#d73b3e]" data-id="element-1253" />
              <span className="text-sm font-medium" data-id="element-1254">
                {filteredJobs.length}+ Active Opportunities
              </span>
            </div>

            <h1 className="font-display text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight" data-id="element-1255">
              Find your next{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1256">
                Laravel job
              </span>
            </h1>

            <p className="text-xl lg:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-1257">
              Browse curated opportunities from companies actively hiring
              certified developers
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto" data-id="element-1258">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1259">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1260" />
              </div>
              <input type="text" className="block w-full pl-12 pr-4 py-4 bg-[#1a1d24] border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] shadow-lg" placeholder="Search for jobs, companies, or skills..." value={filters.search} onChange={e => handleFilterChange('search', e.target.value)} data-id="element-1261" />
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400 mt-8" data-id="element-1262">
              <div className="flex items-center gap-2" data-id="element-1263">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1264" />
                <span data-id="element-1265">100+ active job listings</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1266">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1267" />
                <span data-id="element-1268">50+ hiring companies</span>
              </div>
            </div>
          </div>
        </div>

        {/* Curved divider */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-1269">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-1270">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#0F1115" data-id="element-1271" />
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 relative z-10" data-id="element-1272">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1273">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8" data-id="element-1274">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-6" data-id="element-1275">
              <button className="w-full flex items-center justify-center px-4 py-3 bg-[#1a1d24] border border-gray-800 rounded-full text-white hover:bg-gray-800 transition-colors" onClick={() => setShowFilters(!showFilters)} data-id="element-1276">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-1277" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>

            {/* Filters Sidebar */}
            <div className={`${showFilters ? 'block' : 'hidden'} lg:block lg:col-span-1 space-y-6`} data-id="element-1278">
              <div className="bg-[#1a1d24] p-6 rounded-2xl border border-gray-800 shadow-sm sticky top-24" data-id="element-1279">
                <div className="flex items-center justify-between mb-6" data-id="element-1280">
                  <h3 className="text-lg font-semibold text-white" data-id="element-1281">Filters</h3>
                  <button className="text-sm text-gray-400 hover:text-[#d73b3e] transition-colors" onClick={clearFilters} data-id="element-1282">
                    Clear All
                  </button>
                </div>

                {/* Location Type Filter */}
                <div className="mb-6" data-id="element-1283">
                  <h4 className="text-sm font-medium text-gray-300 mb-3" data-id="element-1284">
                    Location Type
                  </h4>
                  <div className="space-y-2.5" data-id="element-1285">
                    {['remote', 'onsite', 'hybrid'].map(type => <label key={type} className="flex items-center cursor-pointer group" data-id="element-1286">
                        <input type="checkbox" className="h-4 w-4 text-[#d73b3e] focus:ring-[#d73b3e] border-gray-700 rounded bg-[#0F1115]" checked={filters.locationType.includes(type)} onChange={() => handleFilterChange('locationType', type)} data-id="element-1287" />
                        <span className="ml-2.5 text-sm text-gray-400 capitalize group-hover:text-white transition-colors" data-id="element-1288">
                          {type}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Employment Type Filter */}
                <div className="mb-6" data-id="element-1289">
                  <h4 className="text-sm font-medium text-gray-300 mb-3" data-id="element-1290">
                    Employment Type
                  </h4>
                  <div className="space-y-2.5" data-id="element-1291">
                    {['full-time', 'part-time', 'contract', 'freelance'].map(type => <label key={type} className="flex items-center cursor-pointer group" data-id="element-1292">
                          <input type="checkbox" className="h-4 w-4 text-[#d73b3e] focus:ring-[#d73b3e] border-gray-700 rounded bg-[#0F1115]" checked={filters.employmentType.includes(type)} onChange={() => handleFilterChange('employmentType', type)} data-id="element-1293" />
                          <span className="ml-2.5 text-sm text-gray-400 capitalize group-hover:text-white transition-colors" data-id="element-1294">
                            {type.replace('-', ' ')}
                          </span>
                        </label>)}
                  </div>
                </div>

                {/* Experience Level Filter */}
                <div className="mb-6" data-id="element-1295">
                  <h4 className="text-sm font-medium text-gray-300 mb-3" data-id="element-1296">
                    Experience Level
                  </h4>
                  <div className="space-y-2.5" data-id="element-1297">
                    {['entry', 'mid', 'senior', 'lead'].map(level => <label key={level} className="flex items-center cursor-pointer group" data-id="element-1298">
                        <input type="checkbox" className="h-4 w-4 text-[#d73b3e] focus:ring-[#d73b3e] border-gray-700 rounded bg-[#0F1115]" checked={filters.experienceLevel.includes(level)} onChange={() => handleFilterChange('experienceLevel', level)} data-id="element-1299" />
                        <span className="ml-2.5 text-sm text-gray-400 group-hover:text-white transition-colors" data-id="element-1300">
                          {level === 'entry' ? 'Entry Level' : level === 'mid' ? 'Mid Level' : level === 'senior' ? 'Senior Level' : 'Lead / Manager'}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Featured Jobs Filter */}
                <div data-id="element-1301">
                  <label className="flex items-center cursor-pointer group" data-id="element-1302">
                    <input type="checkbox" className="h-4 w-4 text-[#d73b3e] focus:ring-[#d73b3e] border-gray-700 rounded bg-[#0F1115]" checked={filters.featured} onChange={() => handleFilterChange('featured', !filters.featured)} data-id="element-1303" />
                    <span className="ml-2.5 text-sm text-gray-400 group-hover:text-white transition-colors" data-id="element-1304">
                      Featured Jobs Only
                    </span>
                  </label>
                </div>
              </div>

              {/* Post a Job CTA */}
              <div className="bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 p-6 rounded-2xl border border-[#d73b3e]/20 text-center" data-id="element-1305">
                <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1306">
                  Hiring Laravel Developers?
                </h3>
                <p className="text-sm text-gray-400 mb-5" data-id="element-1307">
                  Post a job listing and reach thousands of certified Laravel
                  developers.
                </p>
                <Link to="/post-job" className="inline-block px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-300 shadow-lg" data-id="element-1308">
                  Post a Job
                </Link>
              </div>
            </div>

            {/* Job Listings */}
            <div className="lg:col-span-3 mt-8 lg:mt-0" data-id="element-1309">
              {/* Results summary */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6" data-id="element-1310">
                <div className="mb-4 sm:mb-0" data-id="element-1311">
                  <p className="text-sm text-gray-400" data-id="element-1312">
                    Showing{' '}
                    <span className="font-medium text-white" data-id="element-1313">
                      {filteredJobs.length > 0 ? indexOfFirstJob + 1 : 0}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium text-white" data-id="element-1314">
                      {Math.min(indexOfLastJob, filteredJobs.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium text-white" data-id="element-1315">
                      {filteredJobs.length}
                    </span>{' '}
                    jobs
                  </p>
                </div>
                <div className="flex items-center" data-id="element-1316">
                  <span className="text-sm text-gray-400 mr-2" data-id="element-1317">Sort by:</span>
                  <div className="relative" data-id="element-1318">
                    <select className="appearance-none bg-[#1a1d24] border border-gray-700 rounded-lg pl-3 pr-8 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e]" defaultValue="recent" data-id="element-1319">
                      <option value="recent" data-id="element-1320">Most Recent</option>
                      <option value="salary-high" data-id="element-1321">Highest Salary</option>
                      <option value="salary-low" data-id="element-1322">Lowest Salary</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400" data-id="element-1323">
                      <ChevronDownIcon className="h-4 w-4" data-id="element-1324" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Cards */}
              {currentJobs.length > 0 ? <div className="space-y-6" data-id="element-1325">
                  {currentJobs.map(job => <Link key={job.id} to={`/jobs/${job.id}`} className="block" data-id="element-1326">
                      <div className={`bg-[#1a1d24] rounded-2xl border ${job.featured ? 'border-[#d73b3e] shadow-lg shadow-[#d73b3e]/10' : 'border-gray-800'} overflow-hidden hover:border-gray-700 transition-all duration-300`} data-id="element-1327">
                        {job.featured && <div className="bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white text-xs font-semibold px-4 py-2" data-id="element-1328">
                            ⭐ Featured
                          </div>}
                        <div className="p-6" data-id="element-1329">
                          <div className="sm:flex items-start" data-id="element-1330">
                            <div className="mb-4 sm:mb-0 sm:mr-5 flex-shrink-0" data-id="element-1331">
                              <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center overflow-hidden border border-gray-800" data-id="element-1332">
                                <img src={job.company.logo} alt={`${job.company.name} logo`} className="w-full h-full object-contain p-2" data-id="element-1333" />
                              </div>
                            </div>
                            <div className="flex-grow" data-id="element-1334">
                              <div className="flex flex-col sm:flex-row sm:justify-between" data-id="element-1335">
                                <div data-id="element-1336">
                                  <h3 className="text-xl font-semibold text-white mb-1 hover:text-[#d73b3e] transition-colors" data-id="element-1337">
                                    {job.title}
                                  </h3>
                                  <p className="text-sm text-gray-400 mb-2" data-id="element-1338">
                                    {job.company.name}
                                  </p>
                                </div>
                                <div className="mt-2 sm:mt-0 text-sm font-medium text-[#d73b3e]" data-id="element-1339">
                                  {formatSalary(job)}
                                </div>
                              </div>
                              <div className="mt-4 flex flex-wrap items-center text-sm text-gray-400 gap-y-2" data-id="element-1340">
                                <div className="flex items-center mr-4 mb-0" data-id="element-1341">
                                  <MapPinIcon className="h-4 w-4 mr-1.5 text-gray-500" data-id="element-1342" />
                                  {job.location}
                                  <span className="ml-1.5 px-2 py-0.5 bg-white/5 rounded-full text-xs capitalize border border-gray-800" data-id="element-1343">
                                    {job.locationType}
                                  </span>
                                </div>
                                <div className="flex items-center mr-4 mb-0" data-id="element-1344">
                                  <BriefcaseIcon className="h-4 w-4 mr-1.5 text-gray-500" data-id="element-1345" />
                                  <span className="capitalize" data-id="element-1346">
                                    {job.employmentType.replace('-', ' ')}
                                  </span>
                                </div>
                                <div className="flex items-center mb-0" data-id="element-1347">
                                  <ClockIcon className="h-4 w-4 mr-1.5 text-gray-500" data-id="element-1348" />
                                  {formatRelativeDate(job.postedDate)}
                                </div>
                              </div>
                              <div className="mt-4 flex flex-wrap gap-2" data-id="element-1349">
                                {job.skills.slice(0, 5).map(skill => <span key={skill} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-gray-300 border border-gray-800" data-id="element-1350">
                                    {skill}
                                  </span>)}
                                {job.skills.length > 5 && <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-gray-400 border border-gray-800" data-id="element-1351">
                                    +{job.skills.length - 5} more
                                  </span>}
                              </div>
                              <div className="mt-6" data-id="element-1352">
                                <p className="text-sm text-gray-400 line-clamp-2" data-id="element-1353">
                                  {job.description.substring(0, 150)}
                                  {job.description.length > 150 ? '...' : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>)}
                </div> : <div className="bg-[#1a1d24] p-12 text-center rounded-2xl border border-gray-800" data-id="element-1354">
                  <div className="flex justify-center mb-4" data-id="element-1355">
                    <BriefcaseIcon className="h-16 w-16 text-gray-600" data-id="element-1356" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2" data-id="element-1357">
                    No jobs found
                  </h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto" data-id="element-1358">
                    We couldn't find any jobs matching your current filters. Try
                    adjusting your search criteria.
                  </p>
                  <button onClick={clearFilters} className="px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-300" data-id="element-1359">
                    Clear Filters
                  </button>
                </div>}

              {/* Pagination */}
              {filteredJobs.length > jobsPerPage && <div className="mt-10 flex justify-center" data-id="element-1360">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-1361" />
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white border-t border-gray-800 relative z-10" data-id="element-1362">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1363">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1364">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-1365">
              Need help with your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1366">
                job search?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-10" data-id="element-1367">
              Create a developer profile to get discovered by top companies or
              browse our resources to improve your skills.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1368">
              <Link to="/signup" className="px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1369">
                Create Developer Profile
              </Link>
              <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20" data-id="element-1370">
                Browse Developers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Jobs;